<template>
  <div style="overflow-x:hidden;">

    <!-- ═══ PAGE HERO ═══ -->
    <div class="nf-page-hero">
      <h1>Latest Blog</h1>
      <ul class="nf-breadcrumb">
        <li><router-link to="/">Home</router-link></li>
        <li class="sep"><i class="fas fa-chevron-right" style="font-size:.65rem;"></i></li>
        <li class="active">Blog Posts</li>
      </ul>
    </div>

    <!-- ═══ BLOG CONTENT ═══ -->
    <section class="py-6 px-3 px-lg-5 bg-white">
      <div class="container-fluid">
        <div class="row g-5">

          <!-- Blog grid -->
          <div class="col-12 col-lg-8">
            <div v-if="loading" class="text-center py-5 text-muted">
              <i class="fas fa-spinner fa-spin fa-2x mb-3 d-block text-primary"></i>Loading posts…
            </div>
            <div v-else-if="error" class="text-center py-5">
              <i class="fas fa-exclamation-triangle fa-2x mb-3 d-block text-danger"></i>
              <p class="text-muted">Failed to load posts. Please try again later.</p>
            </div>
            <div v-else class="row g-4">
              <div
                v-for="(blog, i) in blogs"
                :key="blog.id || i"
                class="col-12 col-sm-6"
              >
                <div class="nf-blog-card">
                  <div class="card-img-wrap">
                    <img :src="getImageUrl(blog.image)" :alt="blog.title" />
                    <div class="nf-date-badge bg-primary text-white text-center">
                      <div class="fw-bold fs-5 lh-1">{{ getDay(blog.date) }}</div>
                      <div class="text-uppercase" style="font-size:.65rem;">{{ getMonth(blog.date) }}</div>
                    </div>
                    <div class="nf-cat-badge" v-if="blog.categories?.[0]">
                      {{ blog.categories[0].name }}
                    </div>
                  </div>
                  <div class="p-4">
                    <div class="d-flex flex-wrap gap-3 text-muted small mb-2">
                      <span><i class="bi bi-person me-1"></i>{{ blog.author }}</span>
                      <span><i class="bi bi-calendar3 me-1"></i>{{ getYear(blog.date) }}</span>
                    </div>
                    <router-link
                      :to="`/blog/${blog.slug}`"
                      class="h5 d-block text-dark text-decoration-none lh-sm mb-3 nf-blog-title"
                    >{{ blog.title }}</router-link>
                    <p class="text-muted small mb-3" v-if="blog.excerpt">{{ blog.excerpt.slice(0,100) }}…</p>
                    <router-link :to="`/blog/${blog.slug}`" class="btn btn-sm btn-outline-primary rounded-pill px-3">
                      Read More <i class="bi bi-arrow-right ms-1"></i>
                    </router-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <nav v-if="totalPages > 1" class="d-flex justify-content-center align-items-center gap-2 mt-5 flex-wrap" aria-label="Pagination">
              <button class="btn btn-outline-primary rounded-pill px-3" :disabled="currentPage === 1" @click="prevPage">
                <i class="bi bi-chevron-left me-1"></i>Prev
              </button>
              <button
                v-for="page in totalPages"
                :key="page"
                @click="goToPage(page)"
                class="btn rounded-circle nf-page-btn"
                :class="page === currentPage ? 'btn-primary' : 'btn-outline-primary'"
                style="width:38px;height:38px;padding:0;"
              >{{ page }}</button>
              <button class="btn btn-outline-primary rounded-pill px-3" :disabled="currentPage === totalPages" @click="nextPage">
                Next <i class="bi bi-chevron-right ms-1"></i>
              </button>
            </nav>
          </div>

          <!-- Sidebar -->
          <aside class="col-12 col-lg-4">
            <div class="nf-sidebar-card mb-4">
              <h5 class="nf-sidebar-title">Recent Posts</h5>
              <div
                v-for="post in recentPosts"
                :key="post.id"
                class="nf-recent-post"
              >
                <img :src="getImageUrl(post.image)" :alt="post.title" class="nf-recent-thumb" />
                <div>
                  <router-link :to="`/blog/${post.slug}`" class="text-dark text-decoration-none nf-recent-title">
                    {{ post.title }}
                  </router-link>
                  <div class="text-muted mt-1" style="font-size:.78rem;">
                    <i class="bi bi-calendar3 me-1"></i>{{ getMonth(post.date) }} {{ getYear(post.date) }}
                  </div>
                </div>
              </div>
              <div v-if="!recentPosts.length" class="text-muted small py-2">Loading…</div>
            </div>

            <!-- CTA side card -->
            <div class="nf-cta-aside text-center">
              <i class="fas fa-envelope-open-text fa-2x mb-3" style="color:rgba(255,255,255,0.85);"></i>
              <h5 class="text-white mb-2">Need our expertise?</h5>
              <p class="text-white mb-4" style="font-size:.88rem;opacity:.85;">
                Get in touch and let us co-create impactful solutions together.
              </p>
              <router-link to="/quote" class="btn btn-primary rounded-pill px-4">Request a Service</router-link>
            </div>
          </aside>

        </div>
      </div>
    </section>

  </div>
</template>

<style scoped>
/* Date badge */
.nf-date-badge {
  position:absolute; top:12px; left:12px;
  padding:.4rem .65rem; border-radius:8px;
  box-shadow:0 3px 10px rgba(0,0,0,0.25);
  line-height:1.3;
}
/* Category badge */
.nf-cat-badge {
  position:absolute; bottom:12px; left:12px;
  background:rgba(26,107,74,0.88); color:#fff;
  font-size:.72rem; font-weight:600;
  padding:.25rem .65rem; border-radius:20px;
  letter-spacing:.04em; text-transform:uppercase;
}

/* Blog title hover */
.nf-blog-title:hover { color:var(--nf-green,#1a6b4a) !important; }

/* Sidebar */
.nf-sidebar-card {
  background:#fff;
  border:1px solid var(--nf-border,#dce8e0);
  border-radius:14px;
  padding:1.5rem;
}
.nf-sidebar-title {
  font-family:'Playfair Display',serif;
  font-size:1.15rem;
  color:var(--nf-dark,#111c22);
  padding-bottom:.75rem;
  margin-bottom:1rem;
  border-bottom:2px solid var(--nf-green,#1a6b4a);
}
.nf-recent-post {
  display:flex; align-items:center; gap:.75rem;
  padding:.7rem 0;
  border-bottom:1px solid var(--nf-border,#dce8e0);
}
.nf-recent-post:last-child { border-bottom:none; }
.nf-recent-thumb {
  width:68px; height:68px; border-radius:8px; object-fit:cover; flex-shrink:0;
}
.nf-recent-title {
  font-size:.88rem; font-weight:600; line-height:1.35;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
}
.nf-recent-title:hover { color:var(--nf-green,#1a6b4a) !important; }

/* CTA aside */
.nf-cta-aside {
  background:linear-gradient(135deg,var(--nf-green-dark,#0e4530),var(--nf-green,#1a6b4a));
  border-radius:14px;
  padding:2rem 1.5rem;
}
</style>

<script>
const MEDIA_BASE = process.env.NODE_ENV === 'production'
  ? 'https://media.nexusfocusconsultancy.com'
  : 'http://127.0.0.1:8000';

export default {
  name: 'BlogView',
  data() {
    return {
      blogs: [],
      loading: false,
      error: false,
      currentPage: 1,
      perPage: 6,
      totalCount: 0,
      nextPageUrl: null,
      prevPageUrl: null,
      baseUrl: 'https://admin.nexusfocusconsultancy.com/blog/api/posts/',
      recentPosts: [],
    };
  },
  computed: {
    totalPages() { return Math.ceil(this.totalCount / this.perPage); },
  },
  methods: {
    getImageUrl(image) {
      return image?.includes('http') ? image : `${MEDIA_BASE}${image}`;
    },
    getDay(d)   { return new Date(d).getDate(); },
    getMonth(d) { return new Date(d).toLocaleString('default', { month: 'short' }); },
    getYear(d)  { return new Date(d).getFullYear(); },
    async fetchBlogs(url = this.baseUrl) {
      this.loading = true; this.error = false;
      try {
        const res  = await fetch(url);
        const data = await res.json();
        this.blogs        = data.results || [];
        this.totalCount   = data.count   || 0;
        this.nextPageUrl  = data.next;
        this.prevPageUrl  = data.previous;
        const u = new URL(url);
        const p = u.searchParams.get('page');
        this.currentPage  = p ? parseInt(p) : 1;
      } catch (e) {
        console.error(e);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchRecentPosts() {
      try {
        const res  = await fetch(this.baseUrl + '?limit=4');
        const data = await res.json();
        this.recentPosts = data.results || [];
      } catch (e) {
        console.error(e);
      }
    },
    nextPage() { if (this.nextPageUrl) this.fetchBlogs(this.nextPageUrl); },
    prevPage() { if (this.prevPageUrl) this.fetchBlogs(this.prevPageUrl); },
    goToPage(p) {
      const u = new URL(this.baseUrl);
      u.searchParams.set('page', p);
      this.fetchBlogs(u.toString());
    },
  },
  mounted() {
    this.fetchBlogs();
    this.fetchRecentPosts();
  },
};
</script>
